import { FiMic } from 'react-icons/fi';

export const part1SimpleAnswersData = {
  courseId: 'part1-simple-answers',
  courseType: 'speaking-course',
  title: 'Part 1 Simple Answers',
  icon: FiMic,
  overview: {
    impact: 'Answer Part 1 personal questions naturally and fully',
    focus: 'Giving 2-3 sentence answers using Answer-Reason-Example',
    uses: 'Speaking Part 1 (4-5 minutes)',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Understanding Part 1',
      content: `<p>Part 1 lasts 4-5 minutes. The examiner asks personal questions about familiar topics such as your home, work, studies, hobbies, and daily life. This is your warm-up—it sets the tone for the rest of the test.</p>`,
      points: [
        'You\'ll answer about 12 questions across 3 topic areas.',
        'Questions are simple and personal—there are no trick questions.',
        'The examiner is <strong>not</strong> testing your knowledge; they\'re testing your <strong>English</strong>.',
        'Aim for 2-3 sentences per answer. One-word or one-sentence answers limit your score.',
        'Don\'t memorize long scripts—examiners are trained to spot rehearsed answers.',
      ],
      warning: 'One-word answers like "Yes" or "Football" give the examiner almost nothing to assess. You\'ll score low on Fluency and Coherence.',
    },
    {
      title: 'The Answer-Reason-Example Formula',
      content: `<p>The A-R-E formula is the easiest way to build a complete Part 1 answer quickly:</p>`,
      points: [
        '<strong>Answer:</strong> Give a direct, clear answer to the question.',
        '<strong>Reason:</strong> Say why. Use connectors like "because," "since," or "as."',
        '<strong>Example:</strong> Add a concrete detail or personal example to make it real.',
      ],
      examples: [
        {
          label: 'Q: Do you like cooking?',
          text: '"Yes, I really enjoy cooking. (A) I find it relaxing after a long day at work. (R) For instance, I usually make pasta or stir-fry on weeknights. (E)"',
        },
        {
          label: 'Q: What kind of music do you listen to?',
          text: '"I mostly listen to pop and R&B. (A) I like them because the melodies are catchy and uplifting. (R) For example, I\'ve been listening to a lot of Adele recently. (E)"',
        },
        {
          label: 'Q: Do you prefer reading books or watching films?',
          text: '"I\'d say I prefer watching films. (A) It\'s easier to follow a story visually, especially when I\'m tired. (R) Last week, I watched a great documentary about ocean life. (E)"',
        },
      ],
      warning: 'You don\'t always need all three parts. Sometimes Answer + Reason is enough. The key is to say more than one sentence.',
    },
    {
      title: 'Common Topics and Sample Answers',
      content: `<p>These are the most frequently tested Part 1 topics. Prepare flexible answers, not memorized scripts.</p>`,
      table: {
        headers: ['Topic', 'Typical Question', 'Good Opening'],
        rows: [
          ['Work / Study', 'Do you work or study?', '"I\'m currently studying... / I work as a..."'],
          ['Home / Accommodation', 'Where do you live?', '"I live in... / I\'m from a small city called..."'],
          ['Hobbies', 'What do you do in your free time?', '"In my spare time, I enjoy..."'],
          ['Food', 'What\'s your favourite food?', '"I\'d have to say... because..."'],
          ['Weather', 'What\'s the weather like in your country?', '"Where I\'m from, it\'s generally..."'],
          ['Transport', 'How do you get to work/school?', '"I usually take... / I tend to walk because..."'],
        ],
      },
      points: [
        'Prepare 2-3 flexible sentences per common topic—not a word-for-word script.',
        'Use natural expressions: "I\'d say...", "I\'m quite into...", "I tend to..."',
        'It\'s fine to say you don\'t like something: "To be honest, I\'m not really a fan of..."',
      ],
    },
    {
      title: 'Common Mistakes',
      content: `<p>Avoid these frequent Part 1 errors:</p>`,
      table: {
        headers: ['Mistake', 'Example', 'Fix'],
        rows: [
          ['One-word answer', '"Yes." / "Football."', 'Add a reason: "Yes, because..."'],
          ['Too long / rambling', 'Talking for 45+ seconds on one question', 'Keep it to 2-3 sentences (15-20 seconds)'],
          ['Memorized script', 'Speaking fast with unnatural rhythm', 'Prepare ideas, not word-for-word scripts'],
          ['Off-topic', 'Answering a different question', 'Listen carefully; answer what was asked'],
          ['Too formal', '"I would like to express my preference for..."', 'Part 1 is casual: "I really like..."'],
        ],
      },
      warning: 'If you don\'t understand a question, say "Sorry, could you repeat that?" The examiner will say it again. This won\'t lower your score.',
    },
  ],
  exercises: [
    {
      title: 'Choose the Better Answer',
      type: 'select',
      instructions: 'For each Part 1 question, choose the answer that would score higher.',
      questions: [
        {
          text: 'Q: "Do you enjoy reading?" — Which answer is better?',
          options: [
            '"Yes, I do. I really enjoy reading novels, especially thrillers. I usually read before bed because it helps me unwind."',
            '"Yes."',
            '"Reading is a fundamental pillar of intellectual development and I would like to express..."',
          ],
          answer: '"Yes, I do. I really enjoy reading novels, especially thrillers. I usually read before bed because it helps me unwind."',
          explanation: 'This uses A-R-E naturally: a clear answer, a specific detail, and a reason. The second is too short; the third is unnaturally formal.',
        },
        {
          text: 'Q: "What\'s your favourite season?" — Which answer is better?',
          options: [
            '"I love summer because I can spend time outdoors. For example, I often go to the beach with friends on weekends."',
            '"Summer."',
            '"All seasons have their merits, and I cannot choose one."',
          ],
          answer: '"I love summer because I can spend time outdoors. For example, I often go to the beach with friends on weekends."',
          explanation: 'A full answer with reason and example. "Summer" alone is too short. Avoiding the question wastes speaking time.',
        },
        {
          text: 'Q: "Do you prefer eating at home or in restaurants?" — Which answer is better?',
          options: [
            '"At home."',
            '"I generally prefer eating at home since it\'s healthier and cheaper. I enjoy cooking simple dishes like pasta or grilled chicken."',
            '"Restaurants, because the ambience is superb and the culinary offerings are exceptionally diverse..."',
          ],
          answer: '"I generally prefer eating at home since it\'s healthier and cheaper. I enjoy cooking simple dishes like pasta or grilled chicken."',
          explanation: 'Natural, specific, and the right length. "At home" is too short. The third option is overly formal for Part 1.',
        },
        {
          text: 'Q: "How do you usually travel to work?" — Which answer is better?',
          options: [
            '"I usually take the bus because it\'s convenient and cheap. The journey takes about 20 minutes, so I listen to podcasts on the way."',
            '"I take the bus."',
            '"Transportation is a critical component of urban infrastructure..."',
          ],
          answer: '"I usually take the bus because it\'s convenient and cheap. The journey takes about 20 minutes, so I listen to podcasts on the way."',
          explanation: 'Gives a clear answer with practical details. It\'s natural and well-extended without being too long.',
        },
      ],
    },
    {
      title: 'Complete Answers Using A-R-E',
      type: 'fill-in',
      instructions: 'Fill in the blank to complete each answer with the missing A-R-E component.',
      questions: [
        {
          text: 'Q: "Do you like your job?" — "Yes, I enjoy my work. I find it rewarding because I get to help people every day. For example, last week I ___."',
          answer: 'helped a student prepare for their university interview',
          explanation: 'The Example (E) should be a specific, concrete detail. Any realistic personal example would work here.',
        },
        {
          text: 'Q: "What do you usually do on weekends?" — "I usually spend time with my family. We enjoy ___ because it gives us a chance to catch up."',
          answer: 'cooking together',
          explanation: 'The Reason (R) follows "because." The blank needs a specific activity that logically leads to "catching up."',
        },
        {
          text: 'Q: "Do you enjoy watching sports?" — "To be honest, I\'m not really a fan of sports. I find them ___. I\'d rather spend my free time reading or playing guitar."',
          answer: 'a bit boring to watch',
          explanation: 'The Reason (R) explains why. The negative opinion is fine—just state it naturally and give a reason.',
        },
        {
          text: 'Q: "What kind of weather do you prefer?" — "I prefer sunny weather ___. For instance, I love going for walks by the river when the sun is out."',
          answer: 'because it puts me in a good mood',
          explanation: 'The blank needs a "because" reason connecting the Answer to the Example.',
        },
      ],
    },
    {
      title: 'Identify Problems with Answers',
      type: 'select',
      instructions: 'What is the main problem with each Part 1 answer?',
      questions: [
        {
          text: '"Yes, I like my city very much. It is a place of outstanding natural beauty with a thriving economic sector and a population of approximately 2.5 million inhabitants. The infrastructure has been..."',
          options: [
            'Too long and overly formal for Part 1',
            'Too short',
            'Off-topic',
          ],
          answer: 'Too long and overly formal for Part 1',
          explanation: 'Part 1 answers should be 2-3 sentences in casual, conversational English—not an essay or encyclopedia entry.',
        },
        {
          text: '"Football."',
          options: [
            'Too short — no reason or detail given',
            'Too formal',
            'Off-topic',
          ],
          answer: 'Too short — no reason or detail given',
          explanation: 'One-word answers don\'t demonstrate any speaking ability. Add: "I really enjoy football because..."',
        },
        {
          text: '"Well, that\'s an interesting question. There are many types of food in the world. Some people like Asian food, others prefer Western food. Food is important for our health..."',
          options: [
            'Avoids giving a personal answer (off-topic/generic)',
            'Too short',
            'Grammar is incorrect',
          ],
          answer: 'Avoids giving a personal answer (off-topic/generic)',
          explanation: 'Part 1 asks about YOU. Don\'t give a general lecture. Say what YOU like and why.',
        },
        {
          text: '"MyNameIsMariaAndIComFromBrazilAndIStudyComputerScienceAtTheUniversityOfSaoPauloAndILikeItVeryMuch..."',
          options: [
            'Sounds memorized and rushed — no natural pausing',
            'Too short',
            'Too formal',
          ],
          answer: 'Sounds memorized and rushed — no natural pausing',
          explanation: 'Speaking without pauses or natural rhythm signals a memorized script. Speak naturally with brief pauses between ideas.',
        },
      ],
    },
    {
      title: 'Practice Part 1 Answers',
      type: 'writing',
      instructions: 'Write a 2-3 sentence answer to each Part 1 question using the A-R-E formula.',
      questions: [
        {
          text: 'Q: "What do you do in your free time?"',
          example: 'In my free time, I usually play basketball with friends. I\'ve been playing since high school, and I find it a great way to stay active and relieve stress.',
          tip: 'Answer (what you do) → Reason (why) → Example or Detail (when, with whom, how often).',
        },
        {
          text: 'Q: "Do you like the area where you live?"',
          example: 'Yes, I quite like my neighbourhood. It\'s quiet and green, which is perfect for me because I enjoy morning walks. There\'s also a lovely café nearby where I go every weekend.',
          tip: 'Answer (yes/no + basic opinion) → Reason (what\'s good about it) → Example (a specific feature or habit).',
        },
        {
          text: 'Q: "What type of music do you enjoy?"',
          example: 'I\'m quite into jazz and bossa nova. I love the relaxed vibe—it really helps me concentrate when I\'m studying. My favourite artist at the moment is Norah Jones.',
          tip: 'Answer (genre) → Reason (what you like about it) → Example (an artist, song, or when you listen).',
        },
      ],
    },
    {
      title: 'Match Topics to Good Openers',
      type: 'select',
      instructions: 'Choose the most natural opening phrase for each Part 1 topic.',
      questions: [
        {
          text: 'Topic: Hobbies — "What do you do in your free time?"',
          options: [
            '"In my spare time, I enjoy..."',
            '"The concept of leisure activities encompasses..."',
            '"Free time."',
          ],
          answer: '"In my spare time, I enjoy..."',
          explanation: 'This is natural, direct, and leads smoothly into your answer. The second is too academic; the third is incomplete.',
        },
        {
          text: 'Topic: Work — "Do you work or are you a student?"',
          options: [
            '"I\'m currently working as a... / I\'m studying..."',
            '"Employment is a fundamental aspect of human existence..."',
            '"Work."',
          ],
          answer: '"I\'m currently working as a... / I\'m studying..."',
          explanation: 'Simple, direct, and natural. It answers the question immediately and sets up a follow-on detail.',
        },
        {
          text: 'Topic: Home — "Where do you live?"',
          options: [
            '"I live in... / I\'m from a city called..."',
            '"My residential situation can be described as..."',
            '"City."',
          ],
          answer: '"I live in... / I\'m from a city called..."',
          explanation: 'Conversational and clear. Avoids over-formality and one-word answers.',
        },
      ],
    },
  ],
  tips: [
    'Keep answers to 2-3 sentences — enough to show ability, short enough to stay on track',
    'Use natural language: "I\'m quite into...", "I tend to...", "To be honest..."',
    'It\'s OK to pause briefly — the examiner expects natural thinking time',
    'Don\'t memorize scripts — prepare flexible ideas for common topics instead',
    'If you don\'t understand a question, politely ask the examiner to repeat it',
  ],
  dailyPractice: [
    'Pick a common topic (food, home, hobbies) and answer 3 questions aloud using A-R-E',
    'Record yourself and listen back — check if each answer has Answer + Reason + Example',
    'Time yourself: each answer should be 15-20 seconds (not too short, not too long)',
    'Practice with a friend or the Liwaza AI Speaking partner for real-time feedback',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Part 1 Simple Answers: IELTS Speaking Part 1 format (4-5 mins, personal topics), answering with 2-3 sentences using the Answer-Reason-Example formula. Common topics include work, study, home, hobbies, food. Difficulty 2.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...part1SimpleAnswersData,
      title: part1SimpleAnswersData.title + ' (Foundations)',
      overview: { ...part1SimpleAnswersData.overview, duration: '15-20 minutes' },
      sections: part1SimpleAnswersData.sections.slice(0, Math.max(2, Math.ceil(part1SimpleAnswersData.sections.length / 2))),
      exercises: part1SimpleAnswersData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...part1SimpleAnswersData,
      title: part1SimpleAnswersData.title + ' (Advanced)',
      overview: { ...part1SimpleAnswersData.overview, duration: '30-35 minutes' },
      sections: part1SimpleAnswersData.sections,
      exercises: part1SimpleAnswersData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(part1SimpleAnswersData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return part1SimpleAnswersData;
}
